import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotification } from '../../context/NotificationContext';
import { 
  LayoutDashboard, Users, ShieldAlert, GraduationCap, Trophy, 
  BrainCircuit, Settings, MessageCircle, FileText, 
  MessageSquareQuote, Star, Flame, Palette, SlidersHorizontal, 
  ChevronDown, Code, X, ChevronRight, LogOut, ClipboardList, Bell, UserPlus, CheckSquare
} from 'lucide-react';

export default function Sidebar({ isSidebarOpen, setSidebarOpen, handleLogout }) {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});
  const { notifications, markPathAsRead } = useNotification();

  const getUnreadForPath = (path) => {
    return notifications?.filter(n => !n.isRead && n.link === path).length || 0;
  };

  const getUnreadForGroup = (group) => {
    if (!group.children) return 0;
    return group.children.reduce((sum, child) => sum + getUnreadForPath(child.path), 0);
  };

  const sidebarGroups = [
    {
      id: 'dashboard',
      name: 'الرئيسية',
      path: '/admin/overview',
      icon: <LayoutDashboard className="w-5 h-5" />,
      type: 'standalone'
    },
    {
      id: 'notifications',
      name: 'الإشعارات',
      path: '/admin/notifications',
      icon: <Bell className="w-5 h-5" />,
      type: 'standalone'
    },
    {
      id: 'crm',
      name: 'المستخدمين والعملاء',
      icon: <Users className="w-5 h-5" />,
      type: 'group',
      children: [
        { name: 'إدارة الحسابات', path: '/admin/users', icon: <ShieldAlert className="w-4 h-4" /> },
        { name: 'الطلاب (المسجلين)', path: '/admin/students', icon: <Users className="w-4 h-4" /> },
        { name: 'طلبات التسجيل', path: '/admin/registrations', icon: <UserPlus className="w-4 h-4" /> },
        { name: 'قاعدة العملاء', path: '/admin/customers', icon: <BrainCircuit className="w-4 h-4" /> },
      ]
    },
    {
      id: 'academic',
      name: 'الأكاديمية',
      icon: <GraduationCap className="w-5 h-5" />,
      type: 'group',
      children: [
        { name: 'إدارة الدورات', path: '/admin/courses', icon: <GraduationCap className="w-4 h-4" /> },
        { name: 'التكاليف والمشاريع', path: '/admin/assignments', icon: <CheckSquare className="w-4 h-4" /> },
        { name: 'سجل الدرجات', path: '/admin/gradebook', icon: <Trophy className="w-4 h-4" /> },
        { name: 'طلبات الدورات', path: '/admin/requests', icon: <ClipboardList className="w-4 h-4" /> },
      ]
    },
    {
      id: 'marketing',
      name: 'المحتوى والتسويق',
      icon: <MessageCircle className="w-5 h-5" />,
      type: 'group',
      children: [
        { name: 'إدارة المحتوى', path: '/admin/cms', icon: <Settings className="w-4 h-4" /> },
        { name: 'المقالات', path: '/admin/articles', icon: <FileText className="w-4 h-4" /> },
        { name: 'أعمال الطلاب', path: '/admin/student-works', icon: <Palette className="w-4 h-4" /> },
        { name: 'أتمتة واتساب', path: '/admin/whatsapp', icon: <MessageCircle className="w-4 h-4" /> },
      ]
    },
    {
      id: 'reviews',
      name: 'التقييمات',
      icon: <Star className="w-5 h-5" />,
      type: 'group',
      children: [
        { name: 'آراء الطلاب', path: '/admin/testimonials', icon: <MessageSquareQuote className="w-4 h-4" /> },
        { name: 'تقييمات الدورات', path: '/admin/reviews', icon: <Star className="w-4 h-4" /> },
      ]
    },
    {
      id: 'system',
      name: 'النظام',
      icon: <SlidersHorizontal className="w-5 h-5" />,
      type: 'group',
      children: [
        { name: 'العرض', path: '/admin/offer', icon: <Flame className="w-4 h-4" /> },
        { name: 'إعدادات النظام', path: '/admin/settings', icon: <SlidersHorizontal className="w-4 h-4" /> },
      ]
    }
  ];

  // Auto-expand groups based on current path
  useEffect(() => {
    const newExpanded = { ...expandedGroups };
    sidebarGroups.forEach(group => {
      if (group.type === 'group') {
        const hasActiveChild = group.children.some(child => location.pathname === child.path);
        if (hasActiveChild) {
          newExpanded[group.id] = true;
        }
      }
    });
    setExpandedGroups(newExpanded);
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const navLinkCls = ({ isActive }) => 
    `relative flex items-center justify-between w-full px-5 py-4 rounded-xl font-bold transition-all duration-[400ms] group overflow-hidden ${
      isActive 
      ? 'bg-gradient-to-l from-primary-500/10 to-transparent border-r-4 border-primary-500 shadow-[inset_-10px_0_20px_rgba(230,0,35,0.05)]' 
      : 'text-slate-600 dark:text-zinc-400 hover:bg-slate-100/60 dark:hover:bg-white/5 border-r-4 border-transparent hover:border-slate-200 dark:hover:border-white/10'
    }`;

  const groupBtnCls = (isActive, isOpen) => 
    `relative flex items-center justify-between w-full px-5 py-4 rounded-xl font-bold transition-all duration-[400ms] group overflow-hidden ${
      isActive
      ? 'bg-gradient-to-l from-primary-500/10 to-transparent border-r-4 border-primary-500 shadow-[inset_-10px_0_20px_rgba(230,0,35,0.05)] text-primary-600 dark:text-primary-400'
      : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100/60 dark:hover:bg-white/5 border-r-4 border-transparent hover:border-slate-200 dark:hover:border-white/10'
    }`;

  const childLinkCls = ({ isActive }) => 
    `flex items-center gap-4 px-5 py-3 rounded-xl font-bold text-[0.95rem] transition-all duration-[400ms] group ${
      isActive 
      ? 'bg-primary-500/5 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500' 
      : 'text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-zinc-300 hover:bg-slate-50/50 dark:hover:bg-white/[0.02]'
    }`;

  return (
    <aside className={`fixed lg:sticky top-0 right-0 z-50 w-72 h-screen backdrop-blur-xl bg-white/80 dark:bg-gradient-to-b dark:from-[#0F172A]/90 dark:to-[#020617]/90 border-l border-slate-200/50 dark:border-white/10 flex flex-col transition-transform duration-[400ms] ease-out shadow-[10px_0_50px_-15px_rgba(0,0,0,0.1)] dark:shadow-[10px_0_50px_-15px_rgba(0,0,0,0.5)] ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}>
      
      {/* Brand Header */}
      <div className="p-8 pb-6 border-b border-slate-200/30 dark:border-white/5 relative shrink-0">
        <div className="flex items-center justify-between">
          <div className="relative group">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-l from-primary-500 to-primary-600 drop-shadow-md">Lobab</span>
              <span className="text-3xl font-black tracking-tight text-primary-600 dark:text-primary-500 drop-shadow-md">Admin</span>
            </div>
            <div className="absolute -inset-2 bg-primary-500/20 blur-xl rounded-full opacity-50 group-hover:opacity-80 animate-pulse transition-all duration-700 pointer-events-none"></div>
          </div>
          <button className="lg:hidden p-2 text-slate-500 hover:scale-110 transition-transform" onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2">
           <span className="px-3 py-1 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-[10px] font-extrabold rounded-full border border-red-100 dark:border-red-500/20 uppercase tracking-widest">لوحة الإدارة</span>
        </div>
      </div>

      {/* Navigation Area */}
      <nav className="flex-1 overflow-y-auto px-5 py-6 space-y-4 no-scrollbar">
        {sidebarGroups.map((group) => {
          const isGroupActive = group.type === 'group' && group.children.some(c => location.pathname === c.path);
          
          return (
            <div key={group.id} className="space-y-1.5">
              {group.type === 'standalone' ? (
                <NavLink 
                  to={group.path} 
                  className={navLinkCls}
                  onClick={() => {
                    setSidebarOpen(false);
                    if (getUnreadForPath(group.path) > 0) markPathAsRead(group.path);
                  }}
                >
                  <div className="flex items-center justify-between w-full relative z-10">
                    <div className="flex items-center gap-4">
                      <div className="transition-all duration-300">
                        {group.icon}
                      </div>
                      <span className="text-[1.05rem]">{group.name}</span>
                    </div>
                    {getUnreadForPath(group.path) > 0 && (
                      <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(230,0,35,0.6)] ml-auto" />
                    )}
                  </div>
                  {location.pathname === group.path && (
                    <div className="absolute inset-0 bg-primary-500/5 blur-[20px] scale-150 pointer-events-none"></div>
                  )}
                </NavLink>
              ) : (
                <div className="space-y-1">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={groupBtnCls(isGroupActive, expandedGroups[group.id])}
                  >
                    <div className="flex items-center gap-4 relative z-10">
                      <div className={`transition-all duration-300 ${isGroupActive ? 'text-primary-500 scale-110' : ''}`}>
                        {group.icon}
                      </div>
                      <span className="text-[1.05rem]">{group.name}</span>
                      {getUnreadForGroup(group) > 0 && !expandedGroups[group.id] && (
                        <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(230,0,35,0.6)]" />
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-500 relative z-10 ${expandedGroups[group.id] ? 'rotate-180' : ''}`} />
                    {isGroupActive && (
                      <div className="absolute inset-0 bg-primary-500/5 blur-[20px] scale-150 pointer-events-none"></div>
                    )}
                  </button>

                  <AnimatePresence>
                    {expandedGroups[group.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden border-r-2 border-slate-100/50 dark:border-white/5 mr-4 space-y-1 my-1"
                      >
                        {group.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => {
                              if (window.innerWidth < 1024) setSidebarOpen(false);
                              if (getUnreadForPath(child.path) > 0) markPathAsRead(child.path);
                            }}
                            className={childLinkCls}
                          >
                            <div className="flex items-center justify-between w-full relative z-10">
                              <div className="flex items-center gap-4 py-1">
                                {child.icon}
                                <span>{child.name}</span>
                              </div>
                              {getUnreadForPath(child.path) > 0 && (
                                <span className="px-2 py-0.5 bg-primary-500/10 text-primary-500 text-[10px] rounded-full font-black animate-pulse">
                                  جديد
                                </span>
                              )}
                            </div>
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer Area */}
      <div className="p-7 border-t border-slate-200/50 dark:border-white/10 bg-slate-50/50 dark:bg-black/20 space-y-4 shrink-0">
        <button
          onClick={handleLogout}
          className="group w-full flex items-center gap-4 px-5 py-4 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-500 transition-all duration-[400ms] border border-transparent hover:border-rose-100 dark:hover:border-rose-500/20"
        >
          <LogOut className="w-6 h-6 transition-transform duration-[400ms] group-hover:-translate-x-1" />
          <span className="text-[1.1rem] font-bold tracking-wide">تسجيل الخروج</span>
        </button>
      </div>
    </aside>
  );
}
